import { AtsCareerSiteVacanciesListTypeEnum } from '@/generated/graphql/types';
import { AtsJobsVacancySearchFieldsEnum } from '@/generated/graphql/types.mock';
import {
  VacanciesCardList,
  VacanciesTable,
  convertListToTableVacanciesList,
  createExampleVacancies,
} from '@/shared/components/public-career-site/components/public-list-vacancies';
import type { TListVacanciesProps } from '@/shared/components/public-career-site/components/public-list-vacancies/shared/types';
import { PREVIEW_TABLE } from '@/shared/components/public-career-site/constants';
import { useGetListDepartmentsCareerSite } from '@/shared/components/public-career-site/hooks/use-get-list-departments-career-site/use-get-list-departments-career-site';
import { useParams } from '@/shared/modern-router';
import type {
  ColumnFiltersState,
  PaginationState,
  SearchState,
} from '@crehana/compass.ui/table/tanstack';
import { cn } from '@crehana/compass.ui/utils';
import { useEffect, useState } from 'react';
import { useListJobVacancyCareerSiteAdmin } from '../hooks/use-get-public-list-job-vacancies-career-site/use-get-public-list-job-vacancies-career-site';
import { useAddResourceBundle } from '../i18n/use-add-resource-bundle';
import { useTranslationPublicCareerSite } from '../i18n/use-translations';

const PAGE_SIZE_TABLE = 8;
const PAGE_SIZE_CARD = 9;
export const PublicListVacancies = ({
  title,
  color,
  tableStyle,
  previewType,
  className,
}: TListVacanciesProps) => {
  useAddResourceBundle();
  const { t } = useTranslationPublicCareerSite();
  const { orgSlug, careerSiteId } = useParams();

  // State for search functionality
  const [search, setSearch] = useState<SearchState>('');

  // State for column filters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // State for responsive view
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      if (window.innerWidth < 768) {
        setIsMobile(true); // md breakpoint
        setPagination(prev => ({
          ...prev,
          pageSize: PAGE_SIZE_CARD,
        }));
      } else {
        setIsMobile(false);
        setPagination(prev => ({
          ...prev,
          pageSize: PAGE_SIZE_TABLE,
        }));
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // State for pagination, starting at page 0 with a predefined page size
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize:
      tableStyle ===
        AtsCareerSiteVacanciesListTypeEnum.CareerSiteVacanciesListTable &&
      previewType !== PREVIEW_TABLE.MOBILE &&
      !isMobile
        ? PAGE_SIZE_TABLE
        : PAGE_SIZE_CARD,
  });

  const exampleVacancies = createExampleVacancies(9, t, color);

  // Get selected department IDs from column filters
  const selectedDepartmentIds = columnFilters.find(
    filter => filter.id === 'area',
  )?.value as string | string[] | undefined;

  // Convert string IDs to numbers, handling both single value and array
  const departmentIds = selectedDepartmentIds
    ? Array.isArray(selectedDepartmentIds)
      ? selectedDepartmentIds.map(id => Number(id)).filter(id => !isNaN(id))
      : !isNaN(Number(selectedDepartmentIds))
        ? [Number(selectedDepartmentIds)]
        : []
    : undefined;

  const { data, loading, error, refetch } = useListJobVacancyCareerSiteAdmin({
    input: {
      slug_id: orgSlug as string,
      page: pagination.pageIndex + 1,
      first:
        tableStyle ===
          AtsCareerSiteVacanciesListTypeEnum.CareerSiteVacanciesListTable &&
        previewType !== PREVIEW_TABLE.MOBILE &&
        !isMobile
          ? PAGE_SIZE_TABLE
          : PAGE_SIZE_CARD,
      search:
        search.length >= 3
          ? {
              fields: [AtsJobsVacancySearchFieldsEnum.VacancyTitle],
              query: search, // Search query from state
            }
          : null,
      filter: {
        job_vacancy_department_ids: departmentIds?.length
          ? departmentIds
          : null,
        career_site_uid: careerSiteId as string,
      },
    },
  });
  const { data: departmentsData } = useGetListDepartmentsCareerSite({
    input: {
      slug_id: orgSlug as string,
      career_site_uid: careerSiteId as string,
    },
  });

  // Convert fetched data into table-friendly format
  const tableData = convertListToTableVacanciesList(
    data,
    color,
    exampleVacancies,
    false,
    undefined,
    careerSiteId,
  );
  const rowCount = data?.metadata?.total_count ?? exampleVacancies.length;

  return (
    <div className={cn('tw-w-full tw-h-full tw-overflow-hidden', className)}>
      <div className="tw-w-full tw-h-full  tw-py-4.8  tw-bg-neutral-light-100">
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-mb-2.4">
          <p className="tw-font-h5 tw-font-semibold">{title}</p>
        </div>
        {/* Render appropriate component based on table style, preview type and screen size */}
        {(tableStyle ===
          AtsCareerSiteVacanciesListTypeEnum.CareerSiteVacanciesListTable &&
          previewType !== PREVIEW_TABLE.MOBILE &&
          !isMobile) ||
        previewType === PREVIEW_TABLE.DESKTOP ? (
          <VacanciesTable
            data={tableData}
            loading={loading}
            error={!!error}
            search={search}
            columnFilters={columnFilters}
            pagination={pagination}
            onSearchChange={setSearch}
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onRefetch={refetch}
            title={title}
            color={color}
            previewType={previewType}
            rowCount={rowCount}
            departmentsData={
              departmentsData?.departments
                ?.map(dept => ({
                  id: dept.work_team?.area_id ?? '',
                  name: dept.work_team?.area?.name ?? '',
                }))
                .filter(dept => dept.id && dept.name) ?? undefined
            }
          />
        ) : (
          <VacanciesCardList
            data={tableData}
            loading={loading}
            error={!!error}
            search={search}
            columnFilters={columnFilters}
            pagination={pagination}
            onSearchChange={setSearch}
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onRefetch={refetch}
            title={title}
            color={color}
            previewType={previewType}
            rowCount={rowCount}
            departmentsData={
              departmentsData?.departments?.map(dept => ({
                id: dept.work_team?.area_id ?? '',
                name: dept.work_team?.area?.name ?? '',
              })) ?? undefined
            }
            totalItems={data?.metadata?.total_count}
          />
        )}
      </div>
    </div>
  );
};
